import { GridColumn, GridTotalItem } from './data-grid.model';

/**
 * The shape this app's components already use for `columnDefs`.
 *
 * 136 of the 239 grids render columns with
 *   <dxi-column *ngFor="let f of columnDefs" [dataField]="f.field"
 *               [caption]="f.headerName" [alignment]="f.alignment"
 *               [width]="f.width" [visible]="!f.hide" [fixed]="f.fixed">
 * so the mapping from that object to a GridColumn is fixed and identical
 * everywhere. Doing it once here is what keeps a grid conversion to a template
 * swap plus one line of TypeScript, instead of rewriting 724 column literals.
 */
export interface LegacyColumnDef {
  field?: string;
  headerName?: string;
  alignment?: string;
  width?: number | string;
  minWidth?: number;
  /** Inverted: the legacy shape says "hide", GridColumn says "visible". */
  hide?: boolean;
  fixed?: boolean;
  /** Legacy grids pass `filter: false` to opt a column out of filtering. */
  filter?: boolean;
  cellTemplate?: string;
  dataType?: string;
  [k: string]: any;
}

/**
 * Maps legacy column defs to GridColumn.
 *
 * Unknown keys are carried through untouched: several screens hang their own
 * flags off the column def (`dlte_in`, `updt_in`, `btn_type`, `crncy_in`) and
 * read them back in a cell template, so dropping them would break those
 * templates silently.
 */
export function toGridColumns(defs: LegacyColumnDef[] | null | undefined): GridColumn[] {
  if (!Array.isArray(defs)) { return []; }
  return defs.map(d => {
    const col: GridColumn = {
      ...d,
      dataField: d.field ?? d.dataField,
      caption: d.headerName ?? d.caption,
      alignment: (d.alignment as any) || 'left',
      width: d.width,
      minWidth: d.minWidth,
      visible: d.hide === true ? false : (d.visible !== false),
      fixed: d.fixed === true,
      cellTemplate: d.cellTemplate,
      dataType: d.dataType as any,
    };
    if (d.filter === false) { col.allowFiltering = false; }
    return col;
  });
}

/**
 * The shape the enterprise dashboards use for their SUMMARY arrays
 * (`ttlColumnDefs`, `smryClmns`).
 *
 * Ten grids build footer aggregates from an array like
 *   { headerName: 'Travel Distance', field: 'trvl_dist', alignment: 'Center',
 *     summaryType: 'sum', format: { type: 'fixedPoint', precision: 2 } }
 * and iterated it with `<dxi-total-item *ngFor>`. Mapping it here keeps those
 * conversions to a single template attribute, exactly as [legacyColumns] does
 * for columns.
 */
export interface LegacyTotalItemDef {
  field?: string;
  summaryType?: string;
  /** DevExtreme `valueFormat` - object, function, or absent. */
  format?: any;
  displayFormat?: string;
  [k: string]: any;
}

/**
 * Maps the legacy summary shape to GridTotalItem.
 *
 * Deliberately maps ONLY column / summaryType / valueFormat / displayFormat.
 *
 * `alignment` is NOT carried across, and that is the faithful choice rather
 * than an omission: those templates bound `[alignment]="g.algnmnt"` while the
 * items spell the field `alignment`, so the binding has always been undefined
 * and DevExtreme has always fallen back to the column's own alignment - which
 * is what this grid's footer does too. Mapping `d.alignment` would ACTIVATE an
 * alignment the screens have never shown (and in several arrays its value is
 * 'Center', which is not even a valid DevExtreme alignment).
 *
 * `displayFormat` is read from the item. In these ten grids no item defines it,
 * so it stays undefined - matching the original, where the binding pointed at
 * `currencyINRFormater`/`roundingFormater`, neither of which exists on any of
 * those components.
 */
export function toGridTotalItems(defs: LegacyTotalItemDef[] | null | undefined): GridTotalItem[] {
  if (!Array.isArray(defs)) { return []; }
  return defs
    .filter(d => d && d.field)
    .map(d => ({
      column: d.field as string,
      summaryType: (d.summaryType || 'count') as GridTotalItem['summaryType'],
      valueFormat: d.format,
      displayFormat: d.displayFormat,
    }));
}
